/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { ItemRole, KickerSpec, TableInfoSpec } from "../../../shared/contracts";
import { KickerType } from "../../core/enums";
import { ItemData, toVertex2D } from "../../core/item-data";
import type { Vertex2D } from "../../math/vertex2d";

export class KickerData extends ItemData {
  public kickerType: number = KickerType.KickerHole;
  public center: Vertex2D;
  public radius: number = 25;
  /** Degrees. */
  public scatter: number = 0.0;
  public hitAccuracy: number = 0.7;
  public hitHeight: number = 40.0;
  public orientation: number = 0.0;
  public szMaterial?: string;
  public szSurface?: string;
  public fallThrough: boolean = false;
  public isEnabled: boolean = true;
  /** The engine always uses legacy mode, because the upstream hit mesh is not part of this program. */
  public readonly legacyMode: boolean = true;
  public role: ItemRole = "none";
  /** Degrees. Angle 0 points up the table. */
  public ejectAngle: number = 0;
  /** Units per physics step. */
  public ejectSpeed: number = 10;

  public constructor(itemName: string, center: Vertex2D) {
    super(itemName);
    this.center = center;
  }

  /** Builds the data from a table spec. A field that the spec leaves out keeps its upstream default. */
  public static fromSpec(spec: KickerSpec, _info?: TableInfoSpec): KickerData {
    const data = new KickerData(spec.name, toVertex2D(spec.center));
    data.radius = spec.radius;
    data.kickerType = spec.kickerType ?? data.kickerType;
    data.orientation = spec.orientation ?? data.orientation;
    data.hitAccuracy = spec.hitAccuracy ?? data.hitAccuracy;
    data.hitHeight = spec.hitHeight ?? data.hitHeight;
    data.scatter = spec.scatter ?? data.scatter;
    data.szMaterial = spec.szMaterial;
    data.szSurface = spec.szSurface;
    data.fallThrough = spec.fallThrough ?? data.fallThrough;
    data.isEnabled = spec.isEnabled ?? true;
    data.role = spec.role ?? "none";
    data.ejectAngle = spec.ejectAngle ?? data.ejectAngle;
    data.ejectSpeed = spec.ejectSpeed ?? data.ejectSpeed;
    return data;
  }
}
